import { RoleScopeSchema, isServiceAccountId, isSystemOrServiceAccountId } from '@reflex/shared';
import { z } from 'zod';
import type { OwnerScopeCaller } from '../owner-scope.js';
import { UNSAFE_bypassOwnerScope } from '../owner-scope-unsafe.js';

// --- Portable owner reference (PRD-13) ---
//
// A single discriminated union that represents who owns a resource in a
// portable seed bundle. Source-local ids never appear -- the union uses
// natural identifiers (email, slug, name) that the target can resolve
// during import.
//
// Design decision (CP1): this is a NEW type rather than an extension of
// `SeedCredentialOwnerRef` because (a) credentials lack a `project`
// variant and use a different consumption pattern, and (b) keeping them
// separate avoids breaking existing credential-resolution code.
// `SeedCredentialOwnerRef` can adopt `SeedOwnerRef` in a follow-on.

export const SeedOwnerRefSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('organization') }),
  z.object({ type: z.literal('user'), email: z.string() }),
  z.object({ type: z.literal('team'), slug: z.string() }),
  z.object({ type: z.literal('project'), name: z.string() }),
  z.object({ type: z.literal('system') }),
  z.object({
    type: z.literal('service'),
    // Either service-account family: plugin-seeded `svc-*` bots or
    // user-created `sva_` accounts. Neither is recreated from a bundle;
    // import resolves the id on the target and falls back to org-owned.
    id: z.string().refine(isServiceAccountId, 'must be a service-account id'),
  }),
]);
export type SeedOwnerRef = z.infer<typeof SeedOwnerRefSchema>;

// --- Composite ref schemas for identity/access-control graph ---
//
// Used by Checkpoint 2 providers (users, teams, roles, memberships,
// resource grants) to type their portable cross-references.

export const SeedUserRefSchema = z.object({ email: z.string() });
export type SeedUserRef = z.infer<typeof SeedUserRefSchema>;

export const SeedTeamRefSchema = z.object({ slug: z.string() });
export type SeedTeamRef = z.infer<typeof SeedTeamRefSchema>;

export const SeedRoleRefSchema = z.discriminatedUnion('builtin', [
  z.object({
    builtin: z.literal(true),
    scope: RoleScopeSchema,
    slug: z.string(),
  }),
  z.object({
    builtin: z.literal(false),
    scope: RoleScopeSchema,
    slug: z.string(),
  }),
]);
export type SeedRoleRef = z.infer<typeof SeedRoleRefSchema>;

export const SeedProjectRefSchema = z.object({ name: z.string() });
export type SeedProjectRef = z.infer<typeof SeedProjectRefSchema>;

export const SeedResourceRefSchema = z.object({
  kind: z.string(),
  name: z.string(),
});
export type SeedResourceRef = z.infer<typeof SeedResourceRefSchema>;

// --- Owner resolution result ---

export interface SeedOwnerResolution {
  ownerId: string;
  warning?: string;
}

// --- Export-side helper: ownerId -> SeedOwnerRef ---

/**
 * Lookup functions the export-side owner-ref rewriter needs to convert a
 * source-local `ownerId` to a portable `SeedOwnerRef`. Each function
 * returns the natural key (email, slug, name) for the given id, or
 * `undefined` when the id does not exist.
 */
export interface OwnerRefLookups {
  getUserEmail(userId: string): Promise<string | undefined>;
  getTeamSlug(teamId: string): Promise<string | undefined>;
  getProjectName?(projectId: string): Promise<string | undefined>;
}

/**
 * Determine the owner entity type from an id using the 3-letter prefix
 * convention (`usr_`, `tem_`, `prj_`, `org_`). Non-human accounts — the
 * `'system'` sentinel and both service-account families (`svc-*`, `sva_`) —
 * report as `'system'` so exports route them through the system/service
 * arm. Returns `'unknown'` for anything else.
 */
export function ownerIdKind(
  ownerId: string,
): 'user' | 'team' | 'project' | 'organization' | 'system' | 'unknown' {
  if (isSystemOrServiceAccountId(ownerId)) return 'system';
  if (ownerId.startsWith('usr_')) return 'user';
  if (ownerId.startsWith('tem_')) return 'team';
  if (ownerId.startsWith('prj_')) return 'project';
  if (ownerId.startsWith('org_')) return 'organization';
  return 'unknown';
}

/**
 * Convert a source-environment `ownerId` to a portable `SeedOwnerRef`.
 *
 * - `null` or the org id itself -> `{ type: 'organization' }`
 * - `usr_*` -> `{ type: 'user', email }` via lookup
 * - `tem_*` -> `{ type: 'team', slug }` via lookup
 * - `prj_*` -> `{ type: 'project', name }` via lookup (when `getProjectName` provided)
 * - `system` -> `{ type: 'system' }`
 * - `svc-*` / `sva_*` -> `{ type: 'service', id }`
 * - unresolvable -> `{ type: 'organization' }` + warning
 *
 * Returns both the ref and an optional warning (e.g. when a lookup fails
 * or the owner is a system account).
 */
export async function exportOwnerRef(
  ownerId: string | null,
  organizationId: string,
  lookups: OwnerRefLookups,
): Promise<{ ref: SeedOwnerRef; warning?: string }> {
  if (ownerId === null || ownerId === organizationId) {
    return { ref: { type: 'organization' } };
  }

  const kind = ownerIdKind(ownerId);

  if (kind === 'system') {
    if (ownerId === 'system') return { ref: { type: 'system' } };
    return { ref: { type: 'service', id: ownerId } };
  }

  if (kind === 'organization') {
    return {
      ref: { type: 'organization' },
      warning: `Owner "${ownerId}" is a different organization id than the export scope. Falling back to org-owned.`,
    };
  }

  if (kind === 'user') {
    const email = await lookups.getUserEmail(ownerId);
    if (email) return { ref: { type: 'user', email } };
    return {
      ref: { type: 'organization' },
      warning: `User "${ownerId}" could not be resolved to an email for portable export. Falling back to org-owned.`,
    };
  }

  if (kind === 'team') {
    const slug = await lookups.getTeamSlug(ownerId);
    if (slug) return { ref: { type: 'team', slug } };
    return {
      ref: { type: 'organization' },
      warning: `Team "${ownerId}" could not be resolved to a slug for portable export. Falling back to org-owned.`,
    };
  }

  if (kind === 'project') {
    const name = await lookups.getProjectName?.(ownerId);
    if (name) return { ref: { type: 'project', name } };
    return {
      ref: { type: 'organization' },
      warning: `Project "${ownerId}" could not be resolved to a name for portable export. Falling back to org-owned.`,
    };
  }

  return {
    ref: { type: 'organization' },
    warning: `Owner "${ownerId}" has an unrecognized id format. Falling back to org-owned.`,
  };
}

// --- Shared provider helpers ---

/**
 * Build {@link OwnerRefLookups} from the services typically available on
 * a seed context. Providers pass the result straight to
 * {@link exportOwnerRef}.
 */
export function buildOwnerRefLookups(services: {
  userService?: { getById(id: string): Promise<{ email: string } | null> };
  teamService?: { getById(id: string): Promise<{ slug: string } | null> };
  projectService?: {
    getById(
      id: string,
      organizationId?: string | null,
    ): Promise<{ name: string } | null | undefined>;
  };
  organizationId?: string | null;
}): OwnerRefLookups {
  return {
    async getUserEmail(userId: string) {
      return (await services.userService?.getById(userId))?.email;
    },
    async getTeamSlug(teamId: string) {
      return (await services.teamService?.getById(teamId))?.slug;
    },
    async getProjectName(projectId: string) {
      return (await services.projectService?.getById(projectId, services.organizationId))?.name;
    },
  };
}

interface OwnerRefServiceBag {
  userService?: { getById(id: string): Promise<{ email: string } | null> };
  teamService?: { getById(id: string): Promise<{ slug: string } | null> };
  projectService?: {
    getById(
      id: string,
      organizationId?: string | null,
    ): Promise<{ name: string } | null | undefined>;
  };
}

/**
 * Build {@link OwnerRefLookups} directly from a seed context's service bag.
 *
 * This is the preferred entry point for owner-capable providers: it reads the
 * `userService`, `teamService`, and `projectService` from `ctx.services` using
 * their exported contracts and threads `ctx.organizationId` into the project
 * lookup so `prj_*` owners export as `{ type: 'project', name }`. Providers no
 * longer hand-write per-call service casts.
 */
export function buildOwnerRefLookupsFromContext(ctx: {
  services?: OwnerRefServiceBag | undefined;
  organizationId?: string | null;
}): OwnerRefLookups {
  return buildOwnerRefLookups({
    userService: ctx.services?.userService,
    teamService: ctx.services?.teamService,
    projectService: ctx.services?.projectService,
    organizationId: ctx.organizationId,
  });
}

// --- CP4 post-pass: reassign owner by name ---

/**
 * Adapter callbacks for {@link reassignSeedOwnerByName}. Each provider
 * supplies its own service accessors so the shared helper stays
 * service-agnostic.
 */
export interface ReassignOwnerAdapters<TRow extends { id: string; name: string }> {
  list(): Promise<TRow[]>;
  updateOwner(id: string): Promise<void>;
  matchesRow?: (row: TRow) => boolean;
}

/**
 * Shared CP4 post-pass implementation for owner-capable seed providers.
 *
 * Finds the org-scoped row whose `name` matches the seed item, applies an
 * optional row predicate ({@link ReassignOwnerAdapters.matchesRow}), and
 * calls `updateOwner` to rebind the row to its resolved project owner.
 * Silently no-ops when the service is missing or no row matches.
 *
 * Providers wire this into their `reassignOwner` hook so the
 * find-by-name + update-owner policy lives in one place.
 */
export async function reassignSeedOwnerByName<TRow extends { id: string; name: string }>(
  name: string,
  adapters: ReassignOwnerAdapters<TRow>,
): Promise<void> {
  const rows = await adapters.list();
  const match = rows.find(
    (row) => row.name === name && (adapters.matchesRow ? adapters.matchesRow(row) : true),
  );
  if (!match) return;
  await adapters.updateOwner(match.id);
}

/**
 * Import-side convenience: resolve a portable owner ref to a target
 * `ownerId`. Falls back to org-owned when the ref is absent,
 * `organization`-typed, or `resolveOwner` is not wired.
 */
export async function importOwnerRef(
  opts: { resolveOwner?: (ref: SeedOwnerRef) => Promise<SeedOwnerResolution> },
  ownerRef: SeedOwnerRef | undefined,
  organizationId: string,
): Promise<{ ownerId: string; warning?: string }> {
  if (!ownerRef || ownerRef.type === 'organization') {
    return { ownerId: organizationId };
  }
  if (opts.resolveOwner) {
    return opts.resolveOwner(ownerRef);
  }
  return {
    ownerId: organizationId,
    warning: 'resolveOwner not available; falling back to org-owned.',
  };
}

/**
 * The owner fence every seed provider reads under.
 *
 * Seeding is a platform-operator path — every `/seeds` route is
 * `RouteScope.PlatformAdmin` — and it moves an organization's whole
 * catalog between environments, re-binding each row's owner through
 * {@link importOwnerRef}. Scoping it to one caller's owner fence would
 * silently drop every other member's rows from a "full org" export, so the
 * owner axis is bypassed by design. The org axis is not: every provider
 * still passes a real `organizationId`.
 *
 * Shared rather than re-declared per provider so this stays ONE reviewable
 * decision about the seed framework, instead of ~29 bespoke reason strings
 * nobody can audit as a set.
 */
export function seedOwnerScope(organizationId: string): OwnerScopeCaller {
  return UNSAFE_bypassOwnerScope(
    organizationId,
    "seed export/import moves an org's whole catalog across owners by design; platform-admin only",
  );
}
